import React from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";

import { EmailPreview as Preview } from "../../../../types/onboarding";

/**
 * Words a failed preview. A refusal (4xx) is the send's own precondition, so the
 * send action's mapper says it in the terms the agent already knows. Anything
 * else is the preview failing to load — which the send mappers' fallbacks
 * ("could not be sent") would misstate.
 */
export const previewErrorMessage = (
  err: any,
  refusalMessage: (err: any) => string
): string => {
  const status = err?.response?.status;
  if (typeof status === "number" && status >= 400 && status < 500) {
    return refusalMessage(err);
  }
  return "The email preview could not be loaded. Please try again.";
};

interface EmailPreviewProps {
  preview: Preview | null;
  loading: boolean;
  // Already in plain words — the caller maps the failure with the same mapper
  // its send action uses, since the preview refuses with the same statuses.
  errorMessage?: string | null;
  onRetry?: () => void;
}

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex gap-2 mb-1 text-sm">
    <span className="text-500" style={{ minWidth: "6rem" }}>
      {label}
    </span>
    <span className="font-medium" style={{ wordBreak: "break-word" }}>
      {children}
    </span>
  </div>
);

/**
 * What the customer would receive, exactly as the send would build it (CWA-173).
 *
 * The body is rendered in an iframe with an empty sandbox: an email carries its
 * own global styles, which would restyle the Back Office if injected into the
 * page, and a template is uploaded content that must never run scripts on this
 * origin. The price is that its links are not clickable here, which a review
 * does not need.
 *
 * Station access keys arrive already masked; nothing here could reveal one.
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({
  preview,
  loading,
  errorMessage = null,
  onRetry,
}) => {
  if (loading) {
    return (
      <div data-testid="email-preview-loading">
        <Skeleton width="60%" className="mb-2" />
        <Skeleton width="40%" className="mb-3" />
        <Skeleton height="16rem" />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex flex-column gap-2">
        <Message severity="error" className="w-full" text={errorMessage} />
        {onRetry && (
          <div>
            <Button label="Try again" icon="pi pi-refresh" text onClick={onRetry} />
          </div>
        )}
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div data-testid="email-preview">
      <div className="mb-3">
        <MetaRow label="From">{preview.from}</MetaRow>
        <MetaRow label="To">{preview.to}</MetaRow>
        <MetaRow label="Subject">{preview.subject}</MetaRow>
        {!!preview.attachments.length && (
          <MetaRow label="Attachments">
            <span className="flex flex-wrap gap-2">
              {preview.attachments.map((a) => (
                <span key={a.filename}>
                  <i className="pi pi-paperclip mr-1" />
                  {a.filename}
                </span>
              ))}
            </span>
          </MetaRow>
        )}
      </div>

      {preview.errors.map((issue, i) => (
        <Message
          key={`error-${i}`}
          severity="error"
          className="w-full mb-2"
          text={issue.message}
        />
      ))}
      {preview.warnings.map((issue, i) => (
        // A guide and a certificate can both be missing: same code, twice.
        <Message
          key={`warn-${i}`}
          severity="warn"
          className="w-full mb-2"
          text={issue.message}
        />
      ))}
      {/* Says which layout the customer gets. The warning list already carries
          the reason when the backend could not load the stored template. */}
      {preview.template_source === "FALLBACK" && (
        <div className="text-500 text-sm mb-2">
          This email uses the standard layout, not a stored template.
        </div>
      )}

      {preview.html !== null ? (
        <iframe
          title="Email preview"
          sandbox=""
          srcDoc={preview.html}
          className="w-full border-1 surface-border border-round"
          style={{ height: "60vh" }}
        />
      ) : (
        <Message
          severity="info"
          className="w-full"
          text="No preview of the body is available."
        />
      )}
    </div>
  );
};

export default EmailPreview;
